import React, { useState, useEffect, useCallback } from 'react';
import {
  getEvents,
  getRegistrationCount,
  EVENT_CATEGORIES,
  fmtDateTime,
} from '../../../api/eventsApi';

const TABS = [
  { key: 'calendar', label: 'Calendar View' },
  { key: 'list',     label: 'List View' },
];

const CATEGORY_COLOR = {
  CIRCLE_TIME:         '#0D9488',
  TALK_WORKSHOP:       '#4F46E5',
  SOCIAL_RECREATIONAL: '#EA580C',
  OTHER:               '#6B7280',
};

const CATEGORY_BADGE = {
  CIRCLE_TIME:         'bb-badge bb-badge--teal',
  TALK_WORKSHOP:       'bb-badge bb-badge--indigo',
  SOCIAL_RECREATIONAL: 'bb-badge bb-badge--orange',
  OTHER:               'bb-badge bb-badge--grey',
};

const STATUS_BADGE = {
  DRAFT:     'bb-badge bb-badge--grey',
  PUBLISHED: 'bb-badge bb-badge--green',
  CANCELLED: 'bb-badge bb-badge--red',
  COMPLETED: 'bb-badge bb-badge--blue',
  ARCHIVED:  'bb-badge bb-badge--grey',
};

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];

function TabBar({ activeTab, onSelect }) {
  return (
    <div className="bb-prog-tabs">
      {TABS.map(t => (
        <button
          key={t.key}
          onClick={() => onSelect(t.key)}
          className={`bb-prog-tab${activeTab === t.key ? ' active' : ''}`}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

function getEventsOnDate(events, date) {
  return events.filter(evt => {
    const d = evt.start_datetime?.toDate ? evt.start_datetime.toDate() : new Date(evt.start_datetime);
    return d.getFullYear() === date.getFullYear() &&
           d.getMonth()    === date.getMonth()    &&
           d.getDate()     === date.getDate();
  });
}

function getWeekGroup(evt) {
  const d = evt.start_datetime?.toDate ? evt.start_datetime.toDate() : new Date(evt.start_datetime);
  const now = new Date();
  const msPerDay = 86400000;
  const diff = Math.floor((d - now) / msPerDay);
  if (diff < 0) return null;
  if (diff < 7)  return 'This Week';
  if (diff < 14) return 'Next Week';
  return 'Later';
}

export default function EventCalendar({ orgId, adminId, onNavigateToRegistrations }) {
  const [activeTab, setActiveTab] = useState('calendar');

  const [allEvents, setAllEvents]   = useState([]);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState('');

  // Calendar state
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear]   = useState(new Date().getFullYear());
  const [selectedDate, setSelectedDate] = useState(null);
  const [calFilter, setCalFilter]       = useState('all'); // all | published | draft

  // List state
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [regCounts, setRegCounts]           = useState({}); // eventId -> { confirmed, waitlisted }
  const [countsLoading, setCountsLoading]   = useState(false);

  const loadEvents = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const all = await getEvents(orgId);
      setAllEvents(all || []);
    } catch (e) {
      setError(e.message || 'Failed to load events.');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => { loadEvents(); }, [loadEvents]);

  // Load upcoming events + reg counts for List View
  useEffect(() => {
    if (activeTab !== 'list') return;
    const cutoff = new Date(Date.now() + 90 * 86400000);
    const now = new Date();
    const upcoming = allEvents.filter(e => {
      const d = e.start_datetime?.toDate ? e.start_datetime.toDate() : new Date(e.start_datetime);
      return d >= now && d <= cutoff && e.status !== 'ARCHIVED' && e.status !== 'CANCELLED';
    }).sort((a, b) => {
      const da = a.start_datetime?.toDate ? a.start_datetime.toDate() : new Date(a.start_datetime);
      const db = b.start_datetime?.toDate ? b.start_datetime.toDate() : new Date(b.start_datetime);
      return da - db;
    });
    setUpcomingEvents(upcoming);

    // Load reg counts
    if (upcoming.length > 0) {
      setCountsLoading(true);
      Promise.all(upcoming.map(async evt => {
        try {
          const c = await getRegistrationCount(orgId, evt.id);
          return [evt.id, c];
        } catch {
          return [evt.id, { confirmed: 0, waitlisted: 0 }];
        }
      })).then(results => {
        const map = {};
        results.forEach(([id, c]) => { map[id] = c; });
        setRegCounts(map);
      }).finally(() => setCountsLoading(false));
    }
  }, [activeTab, allEvents, orgId]);

  // ── Calendar grid helpers ─────────────────────────────────────────────────────
  const calFiltered = allEvents.filter(e => {
    if (calFilter === 'published') return e.status === 'PUBLISHED';
    if (calFilter === 'draft')     return e.status === 'DRAFT';
    return e.status !== 'ARCHIVED';
  });

  const firstDay = new Date(currentYear, currentMonth, 1).getDay();
  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const prevMonth = () => {
    if (currentMonth === 0) { setCurrentMonth(11); setCurrentYear(y => y - 1); }
    else setCurrentMonth(m => m - 1);
    setSelectedDate(null);
  };
  const nextMonth = () => {
    if (currentMonth === 11) { setCurrentMonth(0); setCurrentYear(y => y + 1); }
    else setCurrentMonth(m => m + 1);
    setSelectedDate(null);
  };

  const today = new Date();
  const selectedDayEvents = selectedDate
    ? getEventsOnDate(calFiltered, new Date(currentYear, currentMonth, selectedDate))
    : [];

  // ── List View grouping ────────────────────────────────────────────────────────
  const grouped = { 'This Week': [], 'Next Week': [], 'Later': [] };
  upcomingEvents.forEach(evt => {
    const grp = getWeekGroup(evt);
    if (grp) grouped[grp].push(evt);
  });

  return (
    <div className="bb-prog-tool">
      <div className="bb-prog-tool-header">
        <h2>Event Calendar</h2>
        <p>View and navigate events by calendar or list.</p>
      </div>

      <TabBar activeTab={activeTab} onSelect={setActiveTab} />

      {error && <div className="bb-alert bb-alert--error">{error}</div>}
      {loading && <div className="bb-loading-spinner" />}

      {/* ── CALENDAR VIEW ── */}
      {activeTab === 'calendar' && !loading && (
        <div>
          {/* Controls */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <button className="bb-btn-outline-sm" onClick={prevMonth}>&#8249;</button>
              <span style={{ fontWeight: 700, fontSize: 18, minWidth: 180, textAlign: 'center' }}>
                {MONTH_NAMES[currentMonth]} {currentYear}
              </span>
              <button className="bb-btn-outline-sm" onClick={nextMonth}>&#8250;</button>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {['all', 'published', 'draft'].map(f => (
                <button
                  key={f}
                  onClick={() => setCalFilter(f)}
                  className={calFilter === f ? 'bb-btn-primary' : 'bb-btn-outline-sm'}
                  style={{ textTransform: 'capitalize', fontSize: 13 }}
                >
                  {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Grid */}
          <div style={{ border: '1px solid #E5E7EB', borderRadius: 10, overflow: 'hidden' }}>
            {/* Day headers */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', background: '#F3F4F6' }}>
              {DAY_NAMES.map(d => (
                <div key={d} style={{ padding: '8px 4px', textAlign: 'center', fontWeight: 600, fontSize: 12, color: '#374151' }}>{d}</div>
              ))}
            </div>

            {/* Cells */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
              {cells.map((day, idx) => {
                if (!day) return <div key={`empty-${idx}`} style={{ minHeight: 80, borderTop: '1px solid #F3F4F6', background: '#FAFAFA' }} />;
                const cellDate = new Date(currentYear, currentMonth, day);
                const dayEvents = getEventsOnDate(calFiltered, cellDate);
                const isToday = today.getDate() === day && today.getMonth() === currentMonth && today.getFullYear() === currentYear;
                const isSelected = selectedDate === day;
                return (
                  <div
                    key={day}
                    onClick={() => setSelectedDate(isSelected ? null : day)}
                    style={{
                      minHeight: 80, padding: 6, cursor: 'pointer',
                      borderTop: '1px solid #F3F4F6',
                      background: isSelected ? '#F0FDFA' : isToday ? '#F0FDFA' : '#FFF',
                      transition: 'background 0.15s',
                    }}
                  >
                    <div style={{
                      fontWeight: isToday ? 700 : 400,
                      fontSize: 13,
                      color: isToday ? '#0F766E' : '#374151',
                      marginBottom: 4,
                    }}>{day}</div>
                    {dayEvents.slice(0, 3).map(evt => (
                      <div key={evt.id} style={{
                        fontSize: 11, padding: '2px 5px', borderRadius: 3, marginBottom: 2,
                        background: CATEGORY_COLOR[evt.category] || '#6B7280',
                        color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                      }} title={evt.title}>
                        {evt.title}
                      </div>
                    ))}
                    {dayEvents.length > 3 && (
                      <div style={{ fontSize: 10, color: '#9CA3AF' }}>+{dayEvents.length - 3} more</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Day detail panel */}
          {selectedDate && (
            <div style={{ marginTop: 20, border: '1px solid #E2E8F0', borderRadius: 12, padding: 20, background: '#F0FDFA' }}>
              <h4 style={{ margin: '0 0 12px', color: '#0F766E', fontFamily: 'var(--bb-font-heading)', fontWeight: 700 }}>
                {MONTH_NAMES[currentMonth]} {selectedDate}, {currentYear} — {selectedDayEvents.length} event{selectedDayEvents.length !== 1 ? 's' : ''}
              </h4>
              {selectedDayEvents.length === 0 ? (
                <p style={{ color: '#9CA3AF', fontSize: 14 }}>No events on this day.</p>
              ) : (
                selectedDayEvents.map(evt => (
                  <div key={evt.id} style={{
                    background: '#fff', border: '1px solid #E5E7EB', borderRadius: 8,
                    padding: '12px 16px', marginBottom: 10,
                    borderLeft: `4px solid ${CATEGORY_COLOR[evt.category] || '#6B7280'}`,
                  }}>
                    <div style={{ fontWeight: 700, marginBottom: 4 }}>{evt.title}</div>
                    <div style={{ fontSize: 13, color: '#6B7280', marginBottom: 6 }}>
                      {fmtDateTime(evt.start_datetime)} — {fmtDateTime(evt.end_datetime)}
                      {evt.location?.name && <span> · {evt.location.name}</span>}
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <span className={STATUS_BADGE[evt.status] || 'bb-badge bb-badge--grey'}>{evt.status}</span>
                      <span className={CATEGORY_BADGE[evt.category] || 'bb-badge bb-badge--grey'}>
                        {EVENT_CATEGORIES.find(c => c.value === evt.category)?.label || evt.category}
                      </span>
                      <span style={{ fontSize: 12, color: '#9CA3AF' }}>Capacity: {evt.capacity}</span>
                      {onNavigateToRegistrations && (
                        <button className="bb-btn-outline-sm" style={{ fontSize: 12, marginLeft: 'auto' }}
                          onClick={onNavigateToRegistrations}>
                          View Registrations
                        </button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      )}

      {/* ── LIST VIEW ── */}
      {activeTab === 'list' && !loading && (
        <div>
          {upcomingEvents.length === 0 ? (
            <div className="bb-empty-state" style={{ padding: 40, textAlign: 'center', color: '#9CA3AF' }}>
              No upcoming events in the next 90 days.
            </div>
          ) : (
            ['This Week', 'Next Week', 'Later'].map(group => {
              const groupEvents = grouped[group];
              if (groupEvents.length === 0) return null;
              return (
                <div key={group} style={{ marginBottom: 32 }}>
                  <div style={{ fontFamily: 'var(--bb-font-heading)', fontWeight: 700, fontSize: 15, color: '#0F766E', marginBottom: 12, paddingBottom: 6, paddingLeft: '3ch', borderBottom: '2px solid #E2E8F0' }}>
                    {group}
                  </div>
                  {groupEvents.map(evt => {
                    const cnts = regCounts[evt.id] || { confirmed: 0, waitlisted: 0 };
                    const capPct = evt.capacity > 0 ? Math.min((cnts.confirmed / evt.capacity) * 100, 100) : 0;
                    return (
                      <div key={evt.id} style={{
                        background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10,
                        padding: '14px 18px', marginBottom: 10,
                        borderLeft: `4px solid ${CATEGORY_COLOR[evt.category] || '#6B7280'}`,
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
                          <div style={{ flex: 1, minWidth: 200 }}>
                            <div style={{ fontWeight: 700, marginBottom: 4 }}>{evt.title}</div>
                            <div style={{ fontSize: 13, color: '#6B7280' }}>
                              {fmtDateTime(evt.start_datetime)}
                              {evt.location?.name && <span> · {evt.location.name}</span>}
                            </div>
                            {/* Capacity bar */}
                            {!countsLoading && (
                              <div style={{ marginTop: 8 }}>
                                <div style={{ height: 6, borderRadius: 3, background: '#E5E7EB', overflow: 'hidden', width: 160 }}>
                                  <div style={{ height: '100%', width: `${capPct}%`, background: capPct >= 100 ? '#DC2626' : '#059669', transition: 'width 0.3s' }} />
                                </div>
                                <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>
                                  {cnts.confirmed} / {evt.capacity} confirmed
                                </div>
                              </div>
                            )}
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end' }}>
                            <div style={{ display: 'flex', gap: 6 }}>
                              <span className={STATUS_BADGE[evt.status] || 'bb-badge bb-badge--grey'}>{evt.status}</span>
                              <span className={CATEGORY_BADGE[evt.category] || 'bb-badge bb-badge--grey'}>
                                {EVENT_CATEGORIES.find(c => c.value === evt.category)?.label || evt.category}
                              </span>
                            </div>
                            <div style={{ display: 'flex', gap: 6 }}>
                              <button className="bb-btn-outline-sm" style={{ fontSize: 12 }}>Edit</button>
                              {onNavigateToRegistrations && (
                                <button className="bb-btn-outline-sm" style={{ fontSize: 12 }} onClick={onNavigateToRegistrations}>
                                  Registrations
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
